import React, { useCallback, useMemo } from 'react';
import { Typography } from '@mui/material';
import { t } from '../../i18n/t';
import ChartCanvas from './ChartCanvas';
import { CHART_BAR, defaultPlot, drawAxisFrame, formatTick, linMap } from './senseCapChartDraw';

type RhythmogramChartProps = {
    tMs: number[];
    rrMs: number[];
};

const BAR_W = 4;
const GAP = BAR_W / 4;

const RhythmogramChart = ({ tMs, rrMs }: RhythmogramChartProps) => {
    const n = Math.min(tMs.length, rrMs.length);
    const minDrawWidth = useMemo(() => {
        const plot = defaultPlot(1, 1);
        return Math.ceil(plot.left + n * (BAR_W + GAP) + 12);
    }, [n]);

    const draw = useCallback(
        (ctx: CanvasRenderingContext2D, size: { width: number; height: number }) => {
            if (n < 1) return;
            const plot = defaultPlot(size.width, size.height);
            const yMin = 0;
            const yMax = Math.max(...rrMs.slice(0, n), 1) * 1.1;

            drawAxisFrame(ctx, plot, t('sensecap.axisRrMs'), yMin, yMax);

            const pitch = BAR_W + GAP;
            const maxTicks = Math.max(2, Math.floor((n * pitch) / 72));
            const indexStep = Math.max(1, Math.ceil((n - 1) / maxTicks));

            ctx.strokeStyle = '#ffffff';
            ctx.fillStyle = '#ffffff';
            ctx.lineWidth = 1;
            ctx.font = '11px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            for (let i = 0; i < n; i += indexStep) {
                const x = plot.left + i * pitch;
                ctx.beginPath();
                ctx.moveTo(x, plot.bottom);
                ctx.lineTo(x, plot.bottom + 5);
                ctx.stroke();
                ctx.fillText(formatTick(tMs[i]), x, plot.bottom + 6);
            }
            const last = n - 1;
            if (last > 0 && last % indexStep !== 0) {
                const x = plot.left + last * pitch;
                ctx.beginPath();
                ctx.moveTo(x, plot.bottom);
                ctx.lineTo(x, plot.bottom + 5);
                ctx.stroke();
                ctx.fillText(formatTick(tMs[last]), x, plot.bottom + 6);
            }
            ctx.fillText(t('sensecap.axisTimeMs'), plot.left + (n * pitch) / 2, plot.bottom + 20);

            ctx.fillStyle = CHART_BAR;
            for (let i = 0; i < n; i++) {
                const x = plot.left + i * pitch;
                const y = linMap(rrMs[i], yMin, yMax, plot.bottom, plot.top);
                ctx.fillRect(x, y, BAR_W, Math.max(plot.bottom - y, 1));
            }
        },
        [n, tMs, rrMs],
    );

    if (n < 2) {
        return (
            <Typography color="text.secondary" variant="body2">
                {t('sensecap.hrvEmpty')}
            </Typography>
        );
    }

    return <ChartCanvas draw={draw} height={240} minDrawWidth={minDrawWidth} />;
};

export default RhythmogramChart;
